import React, { useEffect, useState } from 'react';
import { wyscoutMatchesAPI } from '@/services/api';
import MatchInfoCard from './MatchInfoCard';
import { StoredMatch, Substitution, GoalEvent, BookingEvent, Props } from './MatchBrowser/types';
import { loadMatch } from './MatchBrowser/loadMatch';
import LoadingState from './MatchBrowser/LoadingState';
import MatchSelect from './MatchBrowser/MatchSelect';
import ErrorMessage from './MatchBrowser/ErrorMessage';

const MatchBrowser: React.FC<Props> = ({ onMatchLoaded }) => {
  const [matches, setMatches] = useState<StoredMatch[]>([]);
  const [teamId, setTeamId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatchId, setSelectedMatchId] = useState<number | ''>('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchInfo, setMatchInfo] = useState<{
    homeTeamName: string;
    awayTeamName: string;
    homeScore: number;
    awayScore: number;
    dateUtc: string;
    venue: string;
    substitutions: Substitution[];
    playerNames: Map<number, string>;
    goals: GoalEvent[];
    bookings: BookingEvent[];
  } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        console.log('[MatchBrowser] Loading matches and team info...');
        const [matchRes, teamRes] = await Promise.all([
          wyscoutMatchesAPI.getStoredMatches(),
          wyscoutMatchesAPI.getTeamInfo(),
        ]);
        console.log(`[MatchBrowser] Got ${matchRes.data.length} matches, teamId=${teamRes.data.teamId}`);
        // Sort by date descending
        const sorted = [...matchRes.data].sort(
          (a, b) => new Date(b.dateUtc).getTime() - new Date(a.dateUtc).getTime()
        );
        setMatches(sorted);
        setTeamId(teamRes.data.teamId);
      } catch (err) {
        console.error('[MatchBrowser] Failed to load matches/team info:', err);
        setError('Failed to load matches');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSelectMatch = async (matchId: number) => {
    setSelectedMatchId(matchId);
    setProcessing(true);
    setMatchInfo(null);
    setError(null);

    try {
      const outcome = await loadMatch(matchId, teamId);
      if (outcome.kind === 'error') {
        setError(outcome.error);
        return;
      }
      setMatchInfo(outcome.matchInfo);
      onMatchLoaded(outcome.result);
    } catch (err) {
      console.error('[MatchBrowser] handleSelectMatch error:', err);
      setError(`Failed to load match: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-3">
      <MatchSelect
        matches={matches}
        teamId={teamId}
        selectedMatchId={selectedMatchId}
        processing={processing}
        onSelectMatch={handleSelectMatch}
      />

      {error && <ErrorMessage message={error} />}

      {matchInfo && <MatchInfoCard {...matchInfo} />}
    </div>
  );
};

export default MatchBrowser;
